import { useMemo } from 'react';
import { Button } from '@astryxdesign/core/Button';
import { Grid } from '@astryxdesign/core/Grid';
import { HStack } from '@astryxdesign/core/HStack';
import { List, ListItem } from '@astryxdesign/core/List';
import { Section } from '@astryxdesign/core/Section';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Heading, Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { CalendarCheckIcon, CaretRightIcon, ClockIcon, MapPinIcon, NavigationArrowIcon, PhoneIcon, TagIcon, TelegramLogoIcon, TimerIcon, WhatsappLogoIcon } from '@phosphor-icons/react';
import { VkIcon } from '@/components/VkIcon';
import { CardIcon } from '@/features/tenant/CardIcon';
import { PreviewBanner } from '@/features/tenant/PreviewBanner';
import { WorksGallery } from '@/features/tenant/WorksGallery';
import { useTenant } from '@/features/tenant/context';
import { ThemeModeControl, ThemeToggleButton } from '@/features/theme/ThemeModeControl';
import { openState } from '@/features/tenant/openState';
import { priceRange } from '@/features/booking/pricing';
import { describeHours, formatDuration, formatPriceFrom } from '@/lib/format';
import { yandexRouteUrl } from '@/lib/maps';
import { formatPhone } from '@/lib/phone';

export function HomePage() {
  const t = useTenant();
  const base = `/s/${t.slug}`;
  const mains = useMemo(() => t.services.filter((s) => s.category === 'main'), [t.services]);
  const mainServices = mains.slice(0, 4);
  // Messengers and social links come from the studio config; a button exists only for a link that is set.
  const messengers = [
    { type: 'telegram', label: 'Telegram', icon: <TelegramLogoIcon size={20} weight="fill" /> },
    { type: 'whatsapp', label: 'WhatsApp', icon: <WhatsappLogoIcon size={20} weight="fill" /> },
    { type: 'vk', label: 'ВКонтакте', icon: <VkIcon size={18} /> },
  ].flatMap((m) => {
    const link = t.contacts.find((c) => c.type === m.type);
    return link ? [{ ...m, href: link.value }] : [];
  });
  const routeUrl = t.address ? yandexRouteUrl(t.address) : null;
  const state = useMemo(() => openState(t), [t]);

  // Three facts a visitor decides on: is it open, how much, how long.
  const bookable = mains.filter((s) => s.bookable && !s.price_note);
  const fromPrice = bookable.length ? formatPriceFrom(bookable.flatMap(priceRange), t.currency, t.locale) : null;
  const fromDuration = bookable.length ? `от ${formatDuration(Math.min(...bookable.map((s) => s.duration_minutes)))}` : null;
  const facts = [
    { icon: ClockIcon, value: state.allDay ? '24/7' : (state.todayHours ?? 'Выходной'), caption: state.allDay ? 'без выходных' : 'сегодня' },
    ...(fromPrice ? [{ icon: TagIcon, value: fromPrice, caption: 'мойка' }] : []),
    ...(fromDuration ? [{ icon: TimerIcon, value: fromDuration, caption: 'по времени' }] : []),
  ];

  return (
    <VStack as="main" gap={6} paddingInline={4} paddingBlockStart={4} className="app-page" maxWidth={720} style={{ marginInline: 'auto' }}>
      <VStack as="header" className="app-hero app-hero-bleed">
        {t.hero ? <img src={t.hero.url} alt={t.hero.alt ?? t.name} fetchPriority="high" className="app-hero-img" /> : null}
        <VStack className="app-hero-scrim" aria-hidden />
        <ThemeToggleButton className="app-hero-corner app-glass-button" />
        <VStack className="app-hero-content" gap={4}>
          <VStack gap={3}>
            <HStack gap={2} align="center" className="app-chip">
              <StatusDot variant={state.open ? 'success' : 'neutral'} label={state.open ? 'Открыто' : 'Закрыто'} />
              <Text size="sm" weight="medium">{state.label}</Text>
            </HStack>
            <HStack gap={3} align="center">
              {t.logo ? <img src={t.logo.url} alt="" className="app-logo" /> : null}
              <VStack gap={0.5}>
                <Heading level={1} type="display-3">{t.name}</Heading>
                {t.tagline ? <Text color="secondary">{t.tagline}</Text> : null}
              </VStack>
            </HStack>
          </VStack>
          <VStack gap={2}>
            <Button label="Записаться онлайн" variant="primary" size="lg" width="100%" className="app-cta" icon={<CalendarCheckIcon size={20} weight="bold" />} href={`${base}/booking`} />
            <HStack gap={2}>
              {t.phone ? <Button label="Позвонить" variant="secondary" width="100%" className="app-glass-button" icon={<PhoneIcon size={18} />} href={`tel:${t.phone}`} /> : null}
              {routeUrl ? <Button label="Маршрут" variant="secondary" width="100%" className="app-glass-button" icon={<NavigationArrowIcon size={18} />} href={routeUrl} target="_blank" /> : null}
            </HStack>
          </VStack>
        </VStack>
      </VStack>

      <PreviewBanner tenant={t} />

      <Grid gap={2} columns={facts.length}>
        {facts.map((f) => (
          <VStack key={f.caption} gap={1} className="app-fact">
            <f.icon size={20} weight="duotone" color="var(--color-icon-accent)" aria-hidden />
            <Text weight="semibold" hasTabularNumbers>{f.value}</Text>
            <Text size="xsm" color="secondary">{f.caption}</Text>
          </VStack>
        ))}
      </Grid>

      {t.description ? <Text color="secondary">{t.description}</Text> : null}

      <VStack as="section" gap={3}>
        <HStack justify="between" align="center">
          <Heading level={2}>Услуги и цены</Heading>
          <Button label="Все услуги" variant="ghost" size="sm" href={`${base}/services`} />
        </HStack>
        <Section padding={1} className="app-panel">
          <List hasDividers>
            {mainServices.map((s) => (
              <ListItem
                key={s.id}
                label={s.name}
                description={formatDuration(s.duration_minutes)}
                endContent={
                  <HStack gap={1.5} align="center">
                    <Text weight="semibold" color="accent" hasTabularNumbers>{formatPriceFrom(priceRange(s), t.currency, t.locale, s.price_note)}</Text>
                    <CaretRightIcon size={16} color="var(--color-icon-secondary)" aria-hidden />
                  </HStack>
                }
                href={`${base}/booking?service=${s.id}`}
              />
            ))}
          </List>
        </Section>
      </VStack>

      {t.works.length > 0 ? (
        <VStack as="section" gap={3}>
          <HStack justify="between" align="end">
            <Heading level={2}>Наши работы</Heading>
            <Text size="sm" color="secondary">{t.works.length} фото · нажмите, чтобы открыть</Text>
          </HStack>
          <WorksGallery works={t.works} />
        </VStack>
      ) : null}

      {t.cards.length > 0 ? (
        <Grid gap={2} columns={{ minWidth: 200, repeat: 'fit' }}>
          {t.cards.map((c) => (
            <HStack key={c.position} gap={3} align="start" className="app-fact">
              <VStack className="app-icon-badge" aria-hidden>
                <CardIcon name={c.icon} size={22} />
              </VStack>
              <VStack gap={0.5}>
                <Text weight="semibold">{c.title}</Text>
                {c.body ? <Text color="secondary" size="sm">{c.body}</Text> : null}
              </VStack>
            </HStack>
          ))}
        </Grid>
      ) : null}

      <VStack as="section" gap={3}>
        <Heading level={2}>Контакты</Heading>
        {messengers.length > 0 ? (
          <Grid gap={2} columns={messengers.length}>
            {messengers.map((m) => (
              <Button key={m.type} label={m.label} variant="secondary" width="100%" icon={m.icon} href={m.href} target="_blank" />
            ))}
          </Grid>
        ) : null}
        <Section padding={1} className="app-panel">
          <List hasDividers>
            <ListItem startContent={<MapPinIcon size={22} color="var(--color-icon-accent)" aria-hidden />} label={t.address} description={routeUrl ? 'Построить маршрут в Яндекс Картах' : undefined} href={routeUrl ?? t.map_url ?? undefined} target="_blank" />
            <ListItem startContent={<ClockIcon size={22} color="var(--color-icon-accent)" aria-hidden />} label={describeHours(t.hours)} description={state.label} />
            {t.phone ? <ListItem startContent={<PhoneIcon size={22} color="var(--color-icon-accent)" aria-hidden />} label={formatPhone(t.phone)} description="Позвонить" href={`tel:${t.phone}`} /> : null}
            {t.contacts
              .filter((c) => c.type === 'phone' && c.value !== t.phone)
              .map((c) => (
                <ListItem key={c.value + c.label} startContent={<PhoneIcon size={22} color="var(--color-icon-accent)" aria-hidden />} label={formatPhone(c.value)} description={c.label} href={`tel:${c.value}`} />
              ))}
          </List>
        </Section>
      </VStack>

      <VStack as="section" gap={3}>
        <Heading level={2}>Оформление</Heading>
        <ThemeModeControl />
      </VStack>

      <HStack justify="center">
        <Button label="Вход для сотрудников" variant="ghost" size="sm" href={`${base}/owner/`} />
      </HStack>
    </VStack>
  );
}
